// src/App.js
import React from "react";
import { Routes, Route, Navigate, BrowserRouter, useLocation } from "react-router-dom";
import {
  Stack,
  Typography
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

import LandingPage from "./pages/LandingPage";
import Signup from "./pages/SignUp";
import Login from "./pages/Login";
// import HomePage from "./pages/HomePage";

import Mutalytics from "./pages/SeqIntel/Mutalytics";
import NucleoRead from "./pages/SeqIntel/NucleoRead";
import NucleoReadResults from "./pages/SeqIntel/NucleoReadResults";
import ArticleMiner from "./ArticleMiner/ArticleMiner";
import ArticleMinerResults from "./ArticleMiner/ArticleMinerResults";
import LitMap from "./pages/BioThread/LITMAP";
import LitmapResults from "./pages/BioThread/LitmapResults";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BioThread from "./pages/BioThread/BioThread";
import Discover from "./pages/BioThread/Discover";
import Results from "./pages/DiscoverResults";
import SeqIntel from "./pages/SeqIntel/SeqIntel";
import ComingSoon from "./components/CommingSoon";
import Support from "./components/Support";
import PrivateRoute from "./PrivateRoute";
import { auth } from "./firebase";
import { useAuthState } from "react-firebase-hooks/auth";

// 🎨 Indigo palette applied globally
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1e3a8a", dark: "#172554" },
    text: { primary: "#1e3a8a", secondary: "#6B7280" },
  },
  typography: {
    fontFamily: "Poppins, system-ui, -apple-system, sans-serif",
    h1: { fontSize: "2.6rem", fontWeight: 700 },
    h2: { fontSize: "1.8rem", fontWeight: 600 },
    h3: { fontSize: "1.3rem", fontWeight: 600 },
    body1: { fontSize: "1rem", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          minHeight: "100vh",
          background: "linear-gradient(135deg, #F9FAFB 0%, #E0E7FF 100%)",
          backgroundAttachment: "fixed",
        },
        html: { minHeight: "100%" },
        "#root": {
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        },
      },
    },
  },
});

function AppContent() {
  const [user, loading] = useAuthState(auth);
  const location = useLocation();

  const hideChrome = ["/login", "/signup"].includes(location.pathname);
  const NAVBAR_OFFSET = 100;

  return (
    <>
      {!hideChrome && <Navbar user={user} />}

      <main
        style={{
          flex: 1,
          width: "100%",
          paddingTop: !hideChrome ? NAVBAR_OFFSET : 0,
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)",
        }}
      >
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />

          {/* Protected routes
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <HomePage />
              </PrivateRoute>
            }
          /> */}

          <Route
            path="/biothread"
            element={
              <PrivateRoute>
                <BioThread />
              </PrivateRoute>
            }
          >
            <Route path="discover" element={<Discover />} />
            <Route path="discover/results" element={<Results />} />

            <Route path="litmap" element={<LitMap />} />
            <Route path="litmap/results" element={<LitmapResults />} />
          </Route>

          <Route
            path="/article"
            element={
              <PrivateRoute>
                <ArticleMiner />
              </PrivateRoute>
            }
          />
          <Route
            path="/article/results"
            element={
              <PrivateRoute>
                <ArticleMinerResults />
              </PrivateRoute>
            }
          />

          <Route
            path="/seqintel"
            element={
              <PrivateRoute>
                <SeqIntel />
              </PrivateRoute>
            }
          >
            <Route path="mutalytics" element={<ComingSoon />} />
            <Route path="nucleoread" element={<NucleoRead />} />
            <Route path="nucleoread/results" element={<NucleoReadResults />} />
            <Route path="mutalytics/results" element={<ComingSoon />} />
          </Route>
          <Route
            path="/support"
            element={
              <PrivateRoute>
                <Support />
              </PrivateRoute>
            }
          />
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {!hideChrome && (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 2, mb: 1, opacity: 0.8,background: "linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)",}} >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" align="center">
            Research Use Only — This tool provides biological interpretations
            that are predictive in nature and require experimental validation.
          </Typography>
        </Stack>
      )}
      {!hideChrome && <Footer />}
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
